import { norm } from '@/lib/text'
import { ALL, type Filters } from './FilterBar'
import type { TransactionRow } from '@/hooks/useTransactions'
import type { Category } from '@/types/db'

// Categorias aceitas ao filtrar por UMA categoria. Escolher o PAI tem que trazer
// os FILHOS junto — os lançamentos ficam nas subcategorias, então o pai sozinho
// não casaria com nada. Retorna null quando não há filtro de categoria.
export function categoryScope(categoryId: string, categories: Category[] | undefined): Set<string> | null {
  if (categoryId === ALL) return null
  const scope = new Set<string>([categoryId])
  for (const c of categories ?? []) if (c.parent_id === categoryId) scope.add(c.id)
  return scope
}

// Formata um valor em pt-BR ("6.130,32") — 1 instância reaproveitada.
const brNum = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

// Representações "cheias" de um valor, para casar um número ESPECÍFICO (com
// vírgula/ponto): "349.50", "349,50" e "6.130,32".
function fullAmountTokens(v: number): string[] {
  const fixed = Math.abs(v).toFixed(2)
  return [fixed, fixed.replace('.', ','), brNum.format(Math.abs(v))]
}

// Aplica os filtros a uma transação. A busca livre varre descrição, categoria,
// conta, tags E o VALOR. Cada termo (separado por espaço) precisa casar em ALGO.
// Valor:
//  - número INTEIRO puro ("94", "30", "349") → casa o COMEÇO da parte inteira do
//    valor (349 → 349,50). NÃO casa centavos (531,94) nem meio de número (194,04).
//  - número com vírgula/ponto ("349,50", "6.130,32") → casa o valor específico.
export function matchTx(t: TransactionRow, f: Filters, catScope?: Set<string> | null): boolean {
  if (f.accountId !== ALL && t.account_id !== f.accountId) return false
  if (catScope && !(t.category_id && catScope.has(t.category_id))) return false
  if (f.status !== ALL && t.status !== f.status) return false
  if (f.kind !== ALL && t.kind !== f.kind) return false
  if (f.tag !== ALL && !(t.tags ?? []).includes(f.tag)) return false

  const q = norm(f.q.trim())
  if (!q) return true

  const textHay = norm([t.description, t.category?.name, t.account?.name, ...(t.tags ?? [])].filter(Boolean).join(' '))
  const amt = Number(t.amount)
  const base = t.amount_base != null ? Number(t.amount_base) : null
  const intParts = [String(Math.trunc(Math.abs(amt))), ...(base != null ? [String(Math.trunc(Math.abs(base)))] : [])]
  const fullNums = [...fullAmountTokens(amt), ...(base != null ? fullAmountTokens(base) : [])]

  const termMatches = (term: string): boolean => {
    if (textHay.includes(term)) return true
    if (/^\d+$/.test(term)) return intParts.some((ip) => ip.startsWith(term)) // inteiro → prefixo da parte inteira
    if (/^[\d]+[.,][\d]+$|^[\d.,]+$/.test(term)) return fullNums.some((n) => n.includes(term)) // valor específico
    return false
  }
  return q.split(/\s+/).every(termMatches)
}
